/**
 * Visual Editor shell: full-screen route with a top bar, the canvas iframe
 * (same-origin srcdoc, driven by EditorBridge), and the selection-driven
 * sidebar. Saves serialize only the dirty anchored fields back into
 * content_json via POST .../visual-edit/ (fail-closed server validation).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Sheet,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/joy';
import {
  ArrowLeft as ArrowBackIcon,
  ArrowCounterClockwise as UndoIcon,
  Desktop as DesktopWindowsIcon,
  DeviceMobile as PhoneIphoneIcon,
  DeviceTablet as TabletMacIcon,
  FloppyDisk as SaveIcon,
  Rocket as PublishIcon,
} from '@phosphor-icons/react';
import toast from 'react-hot-toast';

import { api } from '../../../../lib/api';
import type {
  EditorDocumentResponse,
  EditorFieldKey,
  EditorRegionName,
  EditorSelection,
  VisualEditPayload,
} from '../../../../types/websiteBuilder';
import { EditorBridge } from './editorBridge';
import {
  ChromePanel,
  TemplatePanel,
  HeroFieldPanel,
  ImagePanel,
  ButtonPanel,
  LockedComponentPanel,
  PagePanel,
  SectionPanel,
  TextPanel,
  type PanelContext,
} from './EditorPanels';
import {
  DEVICE_WIDTHS,
  type DeviceKind,
  buildSummary,
  diffContent,
  draftStorageKey,
  fitScale,
} from './visualEditorLogic';

const SCALAR_KEYS: EditorFieldKey[] = ['hero_heading', 'hero_subheading', 'page_title'];

interface UndoSnapshot {
  html: string;
  fields: Set<EditorFieldKey>;
  regions: Set<EditorRegionName>;
  config: Record<string, unknown> | null;
  markers: string[];
  labels: string[];
  title: string | null;
}

export default function VisualEditor() {
  const { projectId, pageId } = useParams<{ projectId: string; pageId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const bridgeRef = useRef<EditorBridge | null>(null);

  const [selection, setSelection] = useState<EditorSelection | null>(null);
  const [device, setDevice] = useState<DeviceKind>('desktop');
  const [canvasWidth, setCanvasWidth] = useState(0);
  const [dirtyFields, setDirtyFields] = useState<Set<EditorFieldKey>>(new Set());
  const [opLabels, setOpLabels] = useState<string[]>([]);
  const [removedMarkers, setRemovedMarkers] = useState<string[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [configPatch, setConfigPatch] = useState<Record<string, any> | null>(null);
  const [pageTitle, setPageTitle] = useState<string | null>(null);
  const [baseUpdatedAt, setBaseUpdatedAt] = useState<string>('');
  const [savedCount, setSavedCount] = useState(0);
  const [undoStack, setUndoStack] = useState<UndoSnapshot[]>([]);
  const pendingUndo = useRef<UndoSnapshot | null>(null);
  const beforeChangeRef = useRef<() => void>(() => {});
  // Stored sources (header/footer/layout) touched this session.
  const [dirtyRegions, setDirtyRegions] = useState<Set<EditorRegionName>>(new Set());
  const editorState = useRef({ fields: dirtyFields, regions: dirtyRegions, config: configPatch, markers: removedMarkers, labels: opLabels, title: pageTitle });
  editorState.current = { fields: dirtyFields, regions: dirtyRegions, config: configPatch, markers: removedMarkers, labels: opLabels, title: pageTitle };
  beforeChangeRef.current = () => {
    if (!pendingUndo.current && bridgeRef.current) {
      pendingUndo.current = { html: bridgeRef.current.snapshotCanvas(), ...editorState.current };
    }
  };
  const baselineRef = useRef<Partial<Record<EditorFieldKey, string>>>({});

  const documentQuery = useQuery({
    queryKey: ['website-builder-editor-document', projectId, pageId],
    queryFn: async () => {
      const response = await api.websiteBuilder.editorDocument(projectId!, pageId!);
      return response.data;
    },
    enabled: !!projectId && !!pageId,
    staleTime: Infinity,
    retry: false,
  });

  const optionsQuery = useQuery({
    queryKey: ['website-builder-component-options', projectId],
    queryFn: async () => (await api.websiteBuilder.componentOptions(projectId!)).data,
    enabled: !!projectId,
    staleTime: 5 * 60_000,
  });

  const projectQuery = useQuery({
    queryKey: ['website-builder-project', projectId],
    queryFn: async () => (await api.websiteBuilder.getProject(projectId!)).data,
    enabled: !!projectId,
  });

  const doc = documentQuery.data;
  const isLegal = !!doc?.page.is_legal;

  const markDirty = useCallback(
    (fieldKey: EditorFieldKey | undefined, opLabel: string, regionName?: EditorRegionName) => {
      if (pendingUndo.current) {
        const snapshot = pendingUndo.current;
        setUndoStack(prev => [...prev.slice(-24), snapshot]);
        pendingUndo.current = null;
      }
      if (fieldKey) {
        setDirtyFields((prev) => new Set(prev).add(fieldKey));
      }
      // An edit inside a stored source dirties that whole region: the server
      // re-derives its placeholders, so it must receive the full region.
      if (regionName) {
        setDirtyRegions((prev) => new Set(prev).add(regionName));
      }
      setOpLabels((prev) => [...prev, opLabel]);
    },
    []
  );

  // ---- canvas boot ----
  const loadDocument = useCallback(
    async (payload: EditorDocumentResponse, resetDirty: boolean) => {
      if (!iframeRef.current) return;
      if (!bridgeRef.current) {
        bridgeRef.current = new EditorBridge(iframeRef.current, {
          onBeforeChange: () => beforeChangeRef.current(),
          onSelect: (sel) => setSelection(sel),
          onTextChanged: () => {
            // inline typing dirties the field of the current selection
            setSelection((current) => {
              if (current?.fieldKey) markDirty(current.fieldKey, 'Edited text', current.regionName);
              return current;
            });
          },
        });
      }
      bridgeRef.current.setRegions(payload.regions || []);
      await bridgeRef.current.load(payload.html);
      // Baseline = serialize-at-load, so diffing sees only user changes.
      const baseline: Partial<Record<EditorFieldKey, string>> = {};
      payload.editable_keys.forEach((key) => {
        const value = SCALAR_KEYS.includes(key)
          ? bridgeRef.current!.serializeScalar(key)
          : bridgeRef.current!.serializeField(key);
        if (value !== null) baseline[key] = value;
      });
      baselineRef.current = baseline;
      setBaseUpdatedAt(payload.page.updated_at);
      if (resetDirty) {
        setDirtyFields(new Set());
        setOpLabels([]);
        setRemovedMarkers([]);
        setConfigPatch(null);
        setPageTitle(null);
        pendingUndo.current = null;
        setUndoStack([]);
        setDirtyRegions(new Set());
        setSelection(null);
      }
    },
    [markDirty]
  );

  useEffect(() => {
    if (doc) void loadDocument(doc, true);
  }, [doc]);

  useEffect(() => () => bridgeRef.current?.destroy(), []);

  // manual-edit warning on open
  useEffect(() => {
    if (doc?.page.html_manually_edited) {
      toast(
        'This page has hand-edited HTML. Saving a visual edit rebuilds the page and will discard those manual changes.',
        { duration: 8000 }
      );
    }
  }, [doc?.page.html_manually_edited]);

  // scale-to-fit: a callback ref, NOT a mount effect — during the initial
  // loading state the canvas container doesn't exist yet, so an effect with
  // [] deps would observe nothing and the scale would stay locked at 1.
  const [canvasEl, setCanvasEl] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!canvasEl) return;
    const observer = new ResizeObserver(() => setCanvasWidth(canvasEl.clientWidth));
    observer.observe(canvasEl);
    setCanvasWidth(canvasEl.clientWidth);
    return () => observer.disconnect();
  }, [canvasEl]);

  const deviceWidth = DEVICE_WIDTHS[device];
  const scale = fitScale(canvasWidth - 16, deviceWidth);

  const isDirty =
    dirtyFields.size > 0 ||
    dirtyRegions.size > 0 ||
    !!configPatch ||
    removedMarkers.length > 0 ||
    pageTitle !== null;
  // How many pages this save will touch, for the Save/Publish affordances.
  const affectedPages = useMemo(() => {
    let count = 1;
    dirtyRegions.forEach((name) => {
      const region = (doc?.regions || []).find((r) => r.name === name);
      if (region) count = Math.max(count, region.affected_pages);
    });
    return count;
  }, [dirtyRegions, doc]);

  // beforeunload guard
  useEffect(() => {
    if (!isDirty) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // ---- save ----
  const collectPayload = useCallback((): VisualEditPayload | null => {
    const bridge = bridgeRef.current;
    if (!bridge) return null;
    const current: Partial<Record<EditorFieldKey, string>> = {};
    dirtyFields.forEach((key) => {
      const value = SCALAR_KEYS.includes(key) ? bridge.serializeScalar(key) : bridge.serializeField(key);
      if (value !== null) current[key] = value;
    });
    const content = diffContent(baselineRef.current, current);
    if (pageTitle !== null) content.page_title = pageTitle;
    if (!Object.keys(content).length && !configPatch && !removedMarkers.length && !dirtyRegions.size) {
      return null;
    }
    // A config/marker-only change still needs ≥1 content field for the API;
    // re-send an unchanged serialized field in that case.
    if (!Object.keys(content).length && !dirtyRegions.size) {
      const anyKey = (Object.keys(baselineRef.current) as EditorFieldKey[])[0];
      if (anyKey) content[anyKey] = baselineRef.current[anyKey] ?? '';
    }
    const regions: Partial<Record<EditorRegionName, string>> = {};
    dirtyRegions.forEach((name) => {
      const html = bridge.serializeRegion(name);
      if (html !== null) regions[name] = html;
    });

    return {
      content,
      ...(configPatch ? { component_config: configPatch } : {}),
      ...(Object.keys(regions).length
        ? { regions, base_chrome_version: doc?.page ? doc.chrome_version ?? null : null }
        : {}),
      ...(removedMarkers.length ? { removed_markers: removedMarkers } : {}),
      base_updated_at: baseUpdatedAt,
      summary: buildSummary(opLabels),
    };
  }, [dirtyFields, dirtyRegions, configPatch, removedMarkers, baseUpdatedAt, opLabels, pageTitle, doc, projectQuery.data]);

  const saveMutation = useMutation({
    mutationFn: async (payload: VisualEditPayload) =>
      (await api.websiteBuilder.visualEdit(projectId!, pageId!, payload)).data,
    onSuccess: async (data) => {
      const touched = data.affected_page_ids?.length ?? 1;
      toast.success(
        touched > 1 ? `Saved — updated ${touched} pages` : 'Saved — your draft is updated'
      );
      if (data.published_page_ids?.length) {
        toast(`Publishing ${data.published_page_ids.length} page(s) to your live site…`);
      }
      setSavedCount((n) => n + 1);
      try {
        window.localStorage.removeItem(draftStorageKey(projectId!, pageId!));
      } catch {
        /* private mode */
      }
      await loadDocument({ ...(doc as EditorDocumentResponse), html: data.html, chrome_version: data.chrome_version, page: { ...(doc as EditorDocumentResponse).page, updated_at: data.page.updated_at } }, true);
      setBaseUpdatedAt(data.page.updated_at);
      queryClient.invalidateQueries({ queryKey: ['website-builder-project', projectId] });
      queryClient.invalidateQueries({ queryKey: ['website-builder-revisions', projectId] });
    },
    onError: (error: unknown) => {
      // The API client throws an Error carrying .responseData (NOT .data);
      // reading the wrong field meant every failure fell through to the
      // generic toast and the 409 conflict flows never ran.
      type ErrBody = { code?: string; error?: string; details?: string[] };
      const thrown = error as { status?: number; responseData?: ErrBody; data?: ErrBody };
      const err = { data: thrown.responseData ?? thrown.data };
      if (err?.data?.code === 'stale_page') {
        if (window.confirm('This page changed since you opened the editor (an AI edit or another tab). Reload the latest version? Your unsaved canvas changes will be lost.')) {
          void documentQuery.refetch().then((result) => result.data && loadDocument(result.data, true));
        }
        return;
      }
      if (err?.data?.code === 'stale_regions') {
        if (
          window.confirm(
            "The site's shared header, footer or layout changed since you opened the editor. Reload the latest version? Your unsaved canvas changes will be lost."
          )
        ) {
          void documentQuery.refetch().then((result) => result.data && loadDocument(result.data, true));
        }
        return;
      }
      if (err?.data?.code === 'manual_edits_present') {
        if (window.confirm('This page has hand-edited HTML. Saving will rebuild the page and discard those manual changes. Save anyway?')) {
          const payload = collectPayload();
          if (payload) saveMutation.mutate({ ...payload, force: true });
        }
        return;
      }
      const details = err?.data?.details;
      toast.error(details?.length ? `Could not save: ${details[0]}` : err?.data?.error || 'Save failed — please try again');
    },
  });

  const handleSave = useCallback(() => {
    bridgeRef.current?.endInlineEdit();
    const payload = collectPayload();
    if (!payload) {
      toast('No changes to save yet');
      return;
    }
    saveMutation.mutate(payload);
  }, [collectPayload, saveMutation]);

  // crash-restore draft persistence (payload-shaped; offered on next open)
  useEffect(() => {
    if (!isDirty || !projectId || !pageId) return;
    const payload = collectPayload();
    if (!payload) return;
    try {
      window.localStorage.setItem(draftStorageKey(projectId, pageId), JSON.stringify(payload));
    } catch {
      /* quota/private mode — crash restore is best-effort */
    }
  }, [isDirty, opLabels.length, collectPayload, projectId, pageId]);

  const refreshPreviewMutation = useMutation({
    mutationFn: async () => api.websiteBuilder.refreshPreview(projectId!),
  });

  const publishMutation = useMutation({
    mutationFn: async () => api.websiteBuilder.deployPage(projectId!, pageId!),
    onSuccess: () => toast.success('Publishing this page to your live site…'),
    onError: () => toast.error('Publish failed — check the site manager'),
  });

  const handleBack = useCallback(() => {
    if (isDirty && !window.confirm('You have unsaved changes. Leave anyway?')) return;
    if (savedCount > 0) {
      refreshPreviewMutation.mutate();
      toast('Updating the preview with your changes…');
    }
    navigate(`/marketing/website-builder/${projectId}/manage?page=${pageId}`);
  }, [isDirty, savedCount, navigate, projectId, pageId, refreshPreviewMutation]);

  const handleUndo = useCallback(() => {
    const bridge = bridgeRef.current;
    const last = undoStack[undoStack.length - 1];
    if (!bridge || !last) return;
    pendingUndo.current = null;
    bridge.restoreCanvas(last.html);
    setDirtyFields(last.fields);
    setDirtyRegions(last.regions);
    setConfigPatch(last.config);
    setRemovedMarkers(last.markers);
    setOpLabels(last.labels);
    setPageTitle(last.title);
    setUndoStack((prev) => prev.slice(0, -1));
    setSelection(null);
  }, [undoStack]);

  // Bridge captures the pre-change canvas; markDirty commits one grouped operation.
  const markDirtyWithUndo = useCallback(
    (fieldKey: EditorFieldKey | undefined, opLabel: string, regionName?: EditorRegionName) => {
      markDirty(fieldKey, opLabel, regionName);
    },
    [markDirty]
  );

  const panelContext: PanelContext | null = useMemo(() => {
    if (!selection || !bridgeRef.current || !doc) return null;
    return {
      selection,
      region: bridgeRef.current.regionFor(selection.regionName),
      bridge: bridgeRef.current,
      vocab: doc.style_vocab,
      design: doc.design,
      pages: (projectQuery.data?.pages ?? []).map((p: { slug: string; title: string }) => ({ slug: p.slug, title: p.title })),
      options: optionsQuery.data,
      markDirty: (fieldKey, label) => {
        markDirtyWithUndo(fieldKey, label, selection.regionName);
      },
      updateComponentConfig: (patch) => {
        beforeChangeRef.current();
        setConfigPatch((prev) => ({
          ...(prev || {}),
          ...patch,
          ...(patch.images ? { images: { ...(prev?.images || {}), ...patch.images } } : {}),
        }));
      },
      addRemovedMarkers: (markers) => setRemovedMarkers((prev) => [...prev, ...markers]),
      clearSelection: () => {
        bridgeRef.current?.clearSelection();
        setSelection(null);
      },
    };
  }, [selection, doc, projectQuery.data, optionsQuery.data, markDirtyWithUndo]);

  // ---- render ----

  if (documentQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (documentQuery.isError || !doc) {
    const err = documentQuery.error as { data?: { error?: string } } | null;
    return (
      <Box sx={{ p: 3 }}>
        <Alert color="danger">{err?.data?.error || 'Could not open the visual editor for this page.'}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate(`/marketing/website-builder/${projectId}/manage`)}>
          Back to site manager
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)' }}>
      {/* Top bar */}
      <Sheet
        variant="outlined"
        sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, flexWrap: 'wrap' }}
      >
        <IconButton size="sm" variant="plain" onClick={handleBack} title="Back to site manager">
          <ArrowBackIcon />
        </IconButton>
        <Typography level="title-md" sx={{ mr: 1 }}>
          {doc.page.title || 'Homepage'}
        </Typography>
        {isDirty && (
          <Chip size="sm" color="warning" variant="soft">
            Unsaved changes
          </Chip>
        )}
        {isLegal && (
          <Chip size="sm" color="neutral" variant="soft">
            Legal text
          </Chip>
        )}
        <Box sx={{ flex: 1 }} />
        <ToggleButtonGroup
          size="sm"
          value={device}
          onChange={(_, value) => value && setDevice(value as DeviceKind)}
        >
          <IconButton value="desktop" title="Desktop">
            <DesktopWindowsIcon />
          </IconButton>
          <IconButton value="tablet" title="Tablet">
            <TabletMacIcon />
          </IconButton>
          <IconButton value="mobile" title="Mobile">
            <PhoneIphoneIcon />
          </IconButton>
        </ToggleButtonGroup>
        <Tooltip title="Undo last change">
          <span>
            <IconButton size="sm" variant="outlined" disabled={!undoStack.length} onClick={handleUndo}>
              <UndoIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip
          title={affectedPages > 1 ? `This save updates ${affectedPages} pages` : ''}
          disableHoverListener={affectedPages <= 1}
        >
          <Button
            size="sm"
            startDecorator={<SaveIcon />}
            loading={saveMutation.isPending}
            disabled={!isDirty}
            onClick={handleSave}
          >
            {affectedPages > 1 ? `Save (${affectedPages} pages)` : 'Save'}
          </Button>
        </Tooltip>
        {projectQuery.data?.status === 'deployed' && (
          <Tooltip title="Publish this page's saved draft to your live site">
            <Button
              size="sm"
              color="success"
              variant="soft"
              startDecorator={<PublishIcon />}
              loading={publishMutation.isPending}
              disabled={isDirty}
              onClick={() => isLegal ? handleBack() : publishMutation.mutate()}
            >
              {isLegal ? 'Review & publish' : 'Publish'}
            </Button>
          </Tooltip>
        )}
      </Sheet>

      {/* Canvas + sidebar */}
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <Box
          ref={setCanvasEl}
          sx={{ flex: 1, overflow: 'auto', bgcolor: 'neutral.100', p: 1 }}
        >
          <Box sx={{ width: deviceWidth * scale, height: '100%', mx: 'auto' }}>
            <Box
              component="iframe"
              ref={iframeRef}
              title="Page canvas"
              sandbox="allow-same-origin"
              sx={{
                width: deviceWidth,
                height: `calc(100% / ${scale})`,
                border: '1px solid',
                borderColor: 'neutral.outlinedBorder',
                borderRadius: 'sm',
                bgcolor: '#fff',
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
              }}
            />
          </Box>
        </Box>
        <Sheet variant="outlined" sx={{ width: 320, flexShrink: 0, overflowY: 'auto', p: 1.5 }}>
          {isLegal && (
            <Alert color="neutral" size="sm" sx={{ mb: 1.5 }}>
              Click any paragraph or heading to edit its text. Save your changes, then return to
              the site manager to review and approve the revised page before publishing.
            </Alert>
          )}
          {!selection || !panelContext ? (
            <PagePanel
              title={pageTitle ?? doc.page.title}
              publishedAt={doc.page.published_at}
              updatedAt={baseUpdatedAt || doc.page.updated_at}
              onTitleChange={(title) => {
                if (title !== doc.page.title) {
                  beforeChangeRef.current();
                  setPageTitle(title);
                  markDirty(undefined, 'Renamed the page');
                }
              }}
            />
          ) : selection.kind === 'locked' ? (
            <LockedComponentPanel ctx={panelContext} />
          ) : selection.kind === 'chrome' ? (
            <ChromePanel projectId={projectId!} region={bridgeRef.current?.regionFor(selection.regionName)} />
          ) : selection.kind === 'template' ? (
            <TemplatePanel region={bridgeRef.current?.regionFor(selection.regionName)} />
          ) : selection.kind === 'image' ? (
            <ImagePanel ctx={panelContext} />
          ) : selection.kind === 'button' ? (
            <ButtonPanel ctx={panelContext} />
          ) : selection.kind === 'section' ? (
            <SectionPanel ctx={panelContext} />
          ) : selection.kind === 'heroField' ? (
            <HeroFieldPanel ctx={panelContext} />
          ) : (
            <TextPanel ctx={panelContext} />
          )}
        </Sheet>
      </Box>
    </Box>
  );
}
